import React, { useState, useEffect, useCallback } from "react";
import {
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
} from "reactstrap";
import axios from "axios";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import moment from "moment";
import { format as formatDate, parseJSON, parseISO } from "date-fns";
import FullScreenMobileView from "../FullScreenMobileView";
import Autocomplete from "@mui/material/Autocomplete";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CameraOutlined from "@mui/icons-material/CameraAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import "./styles/modalStyle.css";
import FileViewDownload from "../FileViewDownload";
import { CircularProgress } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import trashcan from "../../icons/trashcan.svg";
import hydrantIcon from "../../markerIcons/hydrant.svg";
import manholeIcon from "../../markerIcons/manhole.svg";
import trafficSignIcon from "../../markerIcons/trafficSign.svg";
import valveIcon from "../../markerIcons/valve.svg";
import waterMeterIcon from "../../markerIcons/watermeter.svg";
import sewerlineIcon from "../../markerIcons/sewerline.svg";
import waterlineIcon from "../../markerIcons/waterline.svg";
import { formatCoordinateForDisplay } from "../../utils/coordinateFormatter";
import { getNextAssetId, ASSET_ID_FIELD_MAP } from "../../utils/assetIdUtils";
import { suggestFieldValues, findNearestAssets } from "../../utils/aiSuggest";
import FIELD_GROUPS from "../../config/fieldGroups";
import VoiceInput from "../VoiceInput";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import Snackbar from "@mui/material/Snackbar";

/**
 * System/internal fields to exclude when duplicating an asset.
 * These are auto-generated or managed by the system and should not be copied.
 */
const SYSTEM_FIELDS = new Set([
    'GlobalID', 'Created_User', 'Created_Date', 'Last_Editor', 'Last_Edited_Date',
    'ClientID', 'DeleteFacility', 'Latitude', 'Longitude', 'LineJSON',
    'HydrantID', 'ManholeID', 'ValveID', 'TrafficSignID', 'WaterMeterID',
    'WaterLineID', 'SewerLineID', 'DaysSinceLastInspection', 'assetFiles',
]);

// Location fields that should NOT be copied during duplicate - they will be auto-filled by reverse geocoding
const LOCATION_FIELDS = new Set([
    'StreetName', 'SubDivisionName', 'Address', 'Location', 'CouncilDistrict',
]);

/**
 * Convert `AddToMap` class to a functional component with hooks.
 */
function AddToMap({
    assetType,
    data,
    serviceData,
    user,
    markerFormsProps,       // e.g., { moveMarker, EditMarker, etc. }
    addMarkerToMap,
    moveMarker,
    CallFromAddToMapToCustomMap,
    removeLatLngLines,
    clearLinesFunc,
    refreshData,
    refreshDataWithOutClosing,
    removeHightLightForMarker,
    deleteMarker,
    onShowError,
    cancel,
    addedLatLng = {},
    addedLinesLatLng = [],
    // Permission props
    canWrite = true,
    canDelete = true,
    isReadOnly = false,
    // Live preview callback for marker color changes
    onMarkerColorPreview,
    // Reverse-geocoded address fields from map click
    geocodedFields,
    // Callback to clear markerData in parent (for duplicate flow)
    onClearMarkerData,
}) {
    // ------------------------
    //  STATE
    // ------------------------

    // Basic config & UI
    const [activeTab, setActiveTab] = useState("1");
    const [selectedAsset, setSelectedAsset] = useState(false);
    const [pickLocation, setPickLocation] = useState(false);
    const [isLineAsset, setIsLineAsset] = useState(
        assetType && assetType.toLowerCase().includes("line")
    );
    const [showWebcam, setShowWebcam] = useState(false);
    const [fileData, setFileData] = useState(null);
    const [fileMime, setFileMime] = useState(null);

    // Form states
    // We'll unify your "asset" and "assetInspection" naming logic into local states
    const [asset, setAsset] = useState(
        assetType
            ? assetType.includes("WaterMeter") ||
                assetType.includes("TrafficSign") ||
                assetType.includes("WaterLine") ||
                assetType.includes("SewerLine")
                ? assetType
                : capitalize(assetType)
            : "Hydrant"
    );
    const [assetInspection, setAssetInspection] = useState(
        assetType
            ? assetType.includes("WaterMeter") || assetType.includes("TrafficSign")
                ? assetType + "Inspection"
                : capitalize(assetType) + "Inspection"
            : "HydrantInspection"
    );

    const [inputObjectArray, setInputObjectArray] = useState([]);
    const [inspectionObjectArray, setInspectionObjectArray] = useState([]);
    const [isEdit, setIsEdit] = useState(data && data.GlobalID ? true : false);

    // The main record for the current asset
    const [assetRecord, setAssetRecord] = useState(() => {
        // E.g. { ...this.props.data } or empty
        if (data && data.GlobalID) return { ...data };
        return {};
    });

    // File states
    const [inputFiles, setInputFiles] = useState([]);
    const [inspectionInputFiles, setInspectionInputFiles] = useState([]);
    const [tempFile, setTempFile] = useState([]);
    const [removeFileArray, setRemoveFileArray] = useState([]);

    // Inspections
    const [inspections, setInspections] = useState([]);
    const [inspectionUniqueNames, setInspectionUniqueNames] = useState([]);
    const [selectInspection, setSelectInspection] = useState(false);
    const [isEditingInspection, setIsEditingInspection] = useState(false);

    // UI logic for data entry or display
    const [notEditingInfo, setNotEditingInfo] = useState(true);
    const [notEditingInspection, setNotEditingInspection] = useState(true);

    // Movement logic
    const [isMoving, setIsMoving] = useState(false);
    const originalLatLng = {
        lat: data && data.Latitude,
        lng: data && data.Longitude,
    };

    // Errors
    const [isErrors, setIsErrors] = useState(false);
    const [errorList, setErrorList] = useState({});
    const [latlngError, setLatlngError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // AI Assist — tracks which fields were filled by AI suggestions
    const [aiSuggestedFields, setAiSuggestedFields] = useState([]);
    const [aiAssistLoading, setAiAssistLoading] = useState(false);
    const [aiSnackbar, setAiSnackbar] = useState({ open: false, message: "" });

    // Accordion section expansion state (persisted in localStorage per asset type)
    const [expandedSections, setExpandedSections] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(`quickAddSections_${asset}`)) || [];
        } catch {
            return [];
        }
    });


    // markerFilters from localStorage
    const [markerFilters, setMarkerFilters] = useState(() => {
        const temp = localStorage.getItem("markerFilters");
        return temp ? temp.split(",") : [];
    });

    // A small helper to unify user data
    const [userData, setUserData] = useState(() => ({
        user: user.Created_User || user.email || 'Unknown User',
        date: new Date(),
        ClientID: user.ClientID,
    }));

    // Ensure user data is always available
    useEffect(() => {
        if (user && user.Created_User) {
            setUserData(prev => ({
                ...prev,
                user: user.Created_User,
                ClientID: user.ClientID
            }));
        }
    }, [user]);

    // Record for the inspection
    const [inspectionRecord, setInspectionRecord] = useState({});

    // For location picking
    const [LatLng, setLatLng] = useState(addedLatLng);

    // ---------------
    //  HELPERS
    // ---------------

    function capitalize(s) {
        if (!s) return "";
        const lower = s.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }

    const formatDataDate = (date) => {
        if (typeof date === "string") {
            return moment.utc(date).format("YYYY-MM-DD");
        }
        if (parseJSON(date) === "Invalid Date") {
            return formatDate(parseISO(date), "yyyy-MM-dd");
        } else {
            return moment.utc(date).format("YYYY-MM-DD");
        }
    };

    /**
     * Helper to gather the "previous values" for textDropdown fields.
     */
    const getPreviousValues = (optionArray, name) => {
        if (!optionArray) return [];
        return [
            ...new Set(
                optionArray
                    .map((col) => col[name])
                    .filter((x) => x !== undefined && x !== null && x !== "")
            ),
        ];
    };

    // ---------------
    //  LIFE CYCLE
    // ---------------
    // Equivalent to componentDidMount: fetch fields, check user, etc.
    useEffect(() => {
        if (inputObjectArray.length === 0) {
            handleFetchAssetFields(asset);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Equivalent to componentWillReceiveProps (check for changes in assetType, data, etc.)
    useEffect(() => {
        setIsLineAsset(assetType.toLowerCase().includes("line"));
        // Build new local state for user, lat/lng, etc.
        setUserData((prev) => ({
            ...prev,
            user: user.Created_User,
            date: new Date(),
            ClientID: user.ClientID,
        }));
        setLatLng(addedLatLng);

        // If we have an existing GlobalID => editing
        setIsEdit(data && data.GlobalID ? true : false);

        if (data && data.GlobalID) {
            setAssetRecord({ ...data });
        } else {
            // new record — MERGE lat/lng into existing assetRecord to preserve auto-incremented ID
            setAssetRecord((prev) => ({
                ...prev,
                Latitude: addedLatLng ? addedLatLng.lat : null,
                Longitude: addedLatLng ? addedLatLng.lng : null,
                LineJSON: addedLinesLatLng.length
                    ? JSON.stringify(addedLinesLatLng)
                    : null,
            }));
        }

        // Also refetch fields if the assetType changes
        // only trigger this if the assetType changes
        if (assetType && assetType !== asset) {
            handleFetchAssetFields(assetType);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
        setAsset(
            assetType
                ? assetType.includes("WaterMeter") ||
                    assetType.includes("TrafficSign") ||
                    assetType.includes("WaterLine") ||
                    assetType.includes("SewerLine")
                    ? assetType
                    : capitalize(assetType)
                : "Hydrant"
        );
        setAssetInspection(
            assetType
                ? assetType.includes("WaterMeter") || assetType.includes("TrafficSign")
                    ? assetType + "Inspection"
                    : capitalize(assetType) + "Inspection"
                : "HydrantInspection"
        );
    }, [
        assetType,
        data,
        addedLatLng,
        addedLinesLatLng,
        user.Created_User,
        user.ClientID,
        isLineAsset
    ]);

    // Apply reverse-geocoded address fields when they arrive from the map click
    useEffect(() => {
        if (!geocodedFields || isEdit) return;

        setAssetRecord((prev) => {
            const updated = { ...prev };
            const geocodedFieldNames = new Set(Object.keys(geocodedFields));
            
            // First, clear location fields that exist in the form but aren't in the new geocoded data
            // This ensures old location data is removed when clicking a new spot that doesn't have that data
            for (const field of inputObjectArray) {
                if (field.display && LOCATION_FIELDS.has(field.name)) {
                    if (!geocodedFieldNames.has(field.name) && prev[field.name]) {
                        updated[field.name] = '';
                    }
                }
            }
            
            // Set fields that exist in the current form
            // Location fields (StreetName, Location, Address, SubDivisionName) should always be updated
            // when new geocoded data arrives (e.g., user clicks a new spot on the map)
            // Other fields are only set if they're currently empty
            for (const [fieldName, value] of Object.entries(geocodedFields)) {
                const fieldObj = inputObjectArray.find((f) => f.name === fieldName);
                const fieldExists = fieldObj && fieldObj.display;
                const hasPrevValue = !!prev[fieldName];
                const isLocationField = LOCATION_FIELDS.has(fieldName);
                const shouldApply = fieldExists && (isLocationField || !hasPrevValue);
                if (shouldApply) {
                    updated[fieldName] = value;
                }
            }
            return updated;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geocodedFields]);

    // ---------------
    //  LOGIC
    // ---------------
    const handleFetchAssetFields = async (theAsset) => {
        if (!theAsset) return;
        try {
            const { data: res } = await axios.get(`/api/assetFields/${theAsset}`);
            const fields = res.asset;
            if (!fields) {
                window.location = "/login";
                return;
            }
            setInputObjectArray(fields.inputObjectArray);
            setInspectionObjectArray(fields.inspectionObjectArray || []);

            // If we are editing an existing record
            if (data && data[`${theAsset}ID`] && fields.inspectionObjectArray && fields.inspectionObjectArray.length) {
                const inputObject = {
                    asset: `${theAsset}Inspection`,
                    values: {
                        [`${theAsset}ID`]: data[`${theAsset}ID`],
                        ClientID: user.ClientID,
                    },
                    types: fields.inspectionObjectArray.filter(
                        (x) =>
                            x.name === `${theAsset}ID` ||
                            x.name === "ClientID"
                    ),
                };
                const { data: inspectRes } = await axios.post(
                    "/api/assetInspectionSEL",
                    inputObject
                );
                setInspections(inspectRes.result[0] || []);
                setInspectionUniqueNames(inspectRes.uniqueNames || []);
            }
        } catch (err) {
            console.error("Error fetching asset fields:", err);
        }
    };


    /**
     * If we haven't chosen an asset yet, show the asset selection buttons
     * unless we're in edit mode (the user is editing an existing asset).
     */
    const handleAddAsset = async (pickedAsset) => {
        // Is it a line asset?
        const lineTest = pickedAsset.toLowerCase().includes("line");
        setIsLineAsset(lineTest);

        // Update markerFilters in localStorage
        const tempFilters = [...markerFilters];
        if (!tempFilters.includes(pickedAsset)) {
            tempFilters.push(pickedAsset);
        }
        setMarkerFilters(tempFilters);
        localStorage.setItem("markerFilters", tempFilters.toString());
        window.dispatchEvent(new Event("storage"));

        // Update local asset states
        setAsset(pickedAsset);
        setAssetInspection(`${pickedAsset}Inspection`);

        // Reload accordion section preferences for the new asset type
        try {
            setExpandedSections(JSON.parse(localStorage.getItem(`quickAddSections_${pickedAsset}`)) || []);
        } catch {
            setExpandedSections([]);
        }

        // Re-fetch fields for this new asset
        try {
            const { data: res } = await axios.get(`/api/assetFields/${pickedAsset}`);
            const assetFields = res.asset;
            if (!assetFields) {
                window.location = "/login";
                return;
            }
            setInputObjectArray(assetFields.inputObjectArray);
            setInspectionObjectArray(assetFields.inspectionObjectArray || []);

            // If editing an existing record, set that state, otherwise empty
            if (data && data.GlobalID) {
                setAssetRecord((prev) => ({ ...prev, ...data }));
            } else {
                // Auto-suggest the next sequential Client ID for new assets
                const nextId = getNextAssetId(serviceData, pickedAsset);
                if (nextId && nextId.suggestedId) {
                    setAssetRecord({ [nextId.fieldName]: nextId.suggestedId });
                } else {
                    setAssetRecord({});
                }
                setAiSuggestedFields([]);
            }
            setSelectedAsset(true);
        } catch (err) {
            console.error("Error fetching new asset fields:", err);
        }

        // Kick off the map add flow
        addMarkerToMap(pickedAsset);
    };

    // ----------
    //  FORMS
    // ----------

    // A shared event for textfields
    const handleChangeValue = useCallback((e, fieldName, inputAssetName) => {
        setIsErrors(false);
        setErrorList((prev) => ({ ...prev, [fieldName]: false }));

        // Clear AI-suggested highlight when user manually edits a field
        setAiSuggestedFields((prev) => prev.filter((f) => f !== fieldName));

        const newValue = e.target.value;

        // If we're editing the main asset:
        if (inputAssetName === asset) {
            setAssetRecord((prev) => ({
                ...prev,
                [fieldName]: newValue,
            }));
            
            // Live preview for HydrantColor changes (field is named 'Color' in form, maps to 'HydrantColor' in DB)
            if ((fieldName === 'Color' || fieldName === 'HydrantColor') && asset === 'Hydrant' && onMarkerColorPreview && data?.GlobalID) {
                onMarkerColorPreview(data.GlobalID, newValue, asset);
            }
        }
        // If we're editing the asset inspection:
        else if (inputAssetName === assetInspection) {
            setInspectionRecord((prev) => ({
                ...prev,
                [fieldName]: newValue,
            }));
        }
        // Or any other dynamic object name
        else {
            // not used in your code, but just in case
        }
    }, [asset, assetInspection, onMarkerColorPreview, data?.GlobalID]);

    // Format a field value for text display
    const getFieldValue = (obj, field) => {
        if (!obj) return "";
        const v = obj[field];
        if (v === undefined || v === null) return "";
        return v;
    };

    // Toggle an accordion section open/closed and persist to localStorage
    const handleAccordionToggle = (sectionLabel) => {
        setExpandedSections((prev) => {
            const next = prev.includes(sectionLabel)
                ? prev.filter((s) => s !== sectionLabel)
                : [...prev, sectionLabel];
            try {
                localStorage.setItem(`quickAddSections_${asset}`, JSON.stringify(next));
            } catch { /* ignore storage errors */ }
            return next;
        });
    };

    // -------------------------------------------------------
    //  AI Assist — suggest values for empty fields
    // -------------------------------------------------------
    const handleAIAssist = async () => {
        if (!inputObjectArray || !serviceData) return;

        setAiAssistLoading(true);
        setAiSuggestedFields([]);

        try {
            // --- Phase 1: Rule-based (free, instant) ---
            const ruleSuggestions = suggestFieldValues(asset, assetRecord, serviceData, inputObjectArray);
            const ruleFieldNames = Object.keys(ruleSuggestions);

            // Apply rule-based suggestions to the form
            if (ruleFieldNames.length > 0) {
                const newRecord = { ...assetRecord };
                for (const [fieldName, suggestion] of Object.entries(ruleSuggestions)) {
                    if (!newRecord[fieldName] || String(newRecord[fieldName]).trim() === "") {
                        newRecord[fieldName] = suggestion.value;
                    }
                }
                setAssetRecord(newRecord);
                setAiSuggestedFields(ruleFieldNames);
            }

            // --- Phase 2: Gemini for remaining empty fields (optional) ---
            // Build list of still-empty fields after rule-based pass
            const updatedRecord = { ...assetRecord };
            for (const [fn, s] of Object.entries(ruleSuggestions)) {
                updatedRecord[fn] = s.value;
            }

            const stillEmpty = inputObjectArray.filter((field) => {
                if (!field.display) return false;
                if (SYSTEM_FIELDS.has(field.name)) return false;
                if (field.type === "date") return false;
                const val = updatedRecord[field.name];
                return !val || String(val).trim() === "";
            });

            let geminiCount = 0;

            if (stillEmpty.length > 0) {
                try {
                    // Gather nearby assets for context (top 3)
                    const clientIdField = ASSET_ID_FIELD_MAP[asset];
                    const sameTypeAssets = clientIdField
                        ? serviceData.filter((a) => a[clientIdField] != null)
                        : [];
                    const lat = parseFloat(updatedRecord?.Latitude);
                    const lng = parseFloat(updatedRecord?.Longitude);
                    const nearby = findNearestAssets(sameTypeAssets, lat, lng, 3);

                    const emptyFieldDefs = stillEmpty.map((f) => ({
                        name: f.name,
                        displayName: f.displayName,
                        type: f.type,
                        values: f.values || null,
                    }));

                    // Gather already-filled values for context
                    const currentValues = {};
                    for (const f of inputObjectArray) {
                        const v = updatedRecord[f.name];
                        if (v && String(v).trim() !== "") {
                            currentValues[f.name] = v;
                        }
                    }

                    const response = await axios.post("/api/aiSuggest", {
                        assetType: asset,
                        currentValues,
                        emptyFields: emptyFieldDefs,
                        nearbyAssetSample: nearby,
                        geocodedAddress: geocodedFields || null,
                    });

                    if (response.data?.suggestions) {
                        const geminiSuggestions = response.data.suggestions;
                        const geminiFieldNames = Object.keys(geminiSuggestions);
                        if (geminiFieldNames.length > 0) {
                            geminiCount = geminiFieldNames.length;
                            const mergedRecord = { ...updatedRecord };
                            for (const [fn, val] of Object.entries(geminiSuggestions)) {
                                if (!mergedRecord[fn] || String(mergedRecord[fn]).trim() === "") {
                                    mergedRecord[fn] = val;
                                }
                            }
                            setAssetRecord(mergedRecord);
                            setAiSuggestedFields((prev) => [...new Set([...prev, ...geminiFieldNames])]);
                        }
                    }
                } catch (geminiErr) {
                    // Gemini is optional — rule-based results still applied
                    console.log("[AIAssist] Gemini unavailable or failed, using rule-based only:", geminiErr.message);
                }
            }

            // Show result snackbar
            const totalSuggested = ruleFieldNames.length + geminiCount;
            if (totalSuggested > 0) {
                setAiSnackbar({ open: true, message: `✨ AI filled ${totalSuggested} field${totalSuggested > 1 ? "s" : ""}` });
            } else {
                setAiSnackbar({ open: true, message: "No suggestions available for the remaining fields." });
            }
        } catch (err) {
            console.error("[AIAssist] Error:", err);
            setAiSnackbar({ open: true, message: "AI Assist encountered an error." });
        } finally {
            setAiAssistLoading(false);
        }
    };

    // Renders dynamic form input for `inputObjectArray`
    const renderForm = (inputs, inputAssetName, previousValues) => {
        if (!inputs) return null;

        // We'll figure out which record object we're editing
        const record = inputAssetName === asset ? assetRecord : inspectionRecord;

        // Look up field grouping config for this asset type
        const groupConfig = FIELD_GROUPS[inputAssetName];

        // If no grouping config exists (e.g. inspection forms), render flat as before
        if (!groupConfig) {
            return (
                <div>
                    {inputs.map(
                        (field, index) =>
                            field.display && (
                                <div
                                    key={`${field.name}_${index}`}
                                    style={
                                        index === inputs.length - 1
                                            ? { paddingBottom: "15px" }
                                            : {}
                                    }
                                >
                                    {renderSingleField(field, record, inputAssetName, previousValues)}
                                </div>
                            )
                    )}
                    {renderExistingFiles(record, inputAssetName)}
                </div>
            );
        }

        // Build lookup set of all grouped field names for fallback detection
        const allGroupedFields = new Set([
            ...groupConfig.alwaysVisible,
            ...groupConfig.sections.flatMap((s) => s.fields),
        ]);

        // Build field lookup by name for ordering
        const fieldByName = {};
        for (const field of inputs) {
            if (field.display) {
                fieldByName[field.name] = field;
            }
        }

        // 1) Always-visible fields (in the order defined in config)
        const alwaysVisibleFields = groupConfig.alwaysVisible
            .map((name) => fieldByName[name])
            .filter(Boolean);

        // 2) Section fields (in the order defined in config)
        const sectionGroups = groupConfig.sections.map((section) => ({
            label: section.label,
            fields: section.fields
                .map((name) => fieldByName[name])
                .filter(Boolean),
        }));

        // 3) Ungrouped fields — any display:true field NOT in alwaysVisible or any section
        const ungroupedFields = inputs.filter(
            (field) => field.display && !allGroupedFields.has(field.name)
        );

        return (
            <div>
                {/* Always-visible fields */}
                {alwaysVisibleFields.map((field) => (
                    <div key={field.name}>
                        {renderSingleField(field, record, inputAssetName, previousValues)}
                    </div>
                ))}

                {/* Collapsible accordion sections */}
                {sectionGroups.map((section) =>
                    section.fields.length > 0 ? (
                        <Accordion
                            key={section.label}
                            expanded={expandedSections.includes(section.label)}
                            onChange={() => handleAccordionToggle(section.label)}
                            disableGutters
                            sx={{
                                boxShadow: 'none',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px !important',
                                marginTop: '8px',
                                marginBottom: '4px',
                                '&:before': { display: 'none' },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    minHeight: '40px',
                                    backgroundColor: '#f5f7fa',
                                    '& .MuiAccordionSummary-content': {
                                        margin: '8px 0',
                                    },
                                }}
                            >
                                <span style={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#4285f4',
                                    letterSpacing: '0.3px',
                                }}>
                                    {section.label}
                                </span>
                            </AccordionSummary>
                            <AccordionDetails sx={{ padding: '8px 16px 4px' }}>
                                {section.fields.map((field) => (
                                    <div key={field.name}>
                                        {renderSingleField(field, record, inputAssetName, previousValues)}
                                    </div>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ) : null
                )}

                {/* Ungrouped fields (safety fallback for fields added later) */}
                {ungroupedFields.length > 0 && (
                    <Accordion
                        key="__ungrouped"
                        disableGutters
                        sx={{
                            boxShadow: 'none',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px !important',
                            marginTop: '8px',
                            marginBottom: '4px',
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px',
                                backgroundColor: '#f5f7fa',
                                '& .MuiAccordionSummary-content': {
                                    margin: '8px 0',
                                },
                            }}
                        >
                            <span style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#4285f4',
                                letterSpacing: '0.3px',
                            }}>
                                Other Details
                            </span>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: '8px 16px 4px' }}>
                            {ungroupedFields.map((field) => (
                                <div key={field.name}>
                                    {renderSingleField(field, record, inputAssetName, previousValues)}
                                </div>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                )}

                {renderExistingFiles(record, inputAssetName)}
            </div>
        );
    };

    const renderSingleField = (field, record, inputAssetName, previousValues) => {
        const value = record[field.name];
        const showError = isErrors && errorList[field.name];
        const isAiSuggested = aiSuggestedFields.includes(field.name);
        const aiHighlightStyle = isAiSuggested
            ? { borderLeft: "3px solid #4285f4", backgroundColor: "#f0f4ff" }
            : {};
        const sharedProps = {
            error: showError,
            helperText: showError
                ? `${field.displayName} is required`
                : isAiSuggested
                ? "✨ AI suggested"
                : null,
            required: field.required,
        };

        switch (field.type) {
            case "dropdown":
                return (
                    <TextField
                        id={field.name}
                        autoComplete="off"
                        select
                        fullWidth
                        size="small"
                        label={field.displayName}
                        variant="outlined"
                        {...sharedProps}
                        InputLabelProps={{ style: { color: "#4285f4" } }}
                        InputProps={{
                            style: { marginBottom: "10px", ...aiHighlightStyle },
                        }}
                        SelectProps={{
                            MenuProps: {
                                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                getContentAnchorEl: null,
                            },
                        }}
                        value={value === true ? "Yes" : value === false ? "No" : value || ""}
                        onChange={(e) => handleChangeValue(e, field.name, inputAssetName)}
                    >
                        <MenuItem value="" hidden />
                        {field.values.split(", ").map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </TextField>
                );

            case "textDropdown":
                return (
                    <Autocomplete
                        id={field.name}
                        freeSolo
                        fullWidth
                        size="small"
                        // className="without-padding-autocomplete"
                        options={field.name === "Inspector" ? inspectionUniqueNames : getPreviousValues(previousValues, field.name)}
                        value={value || ""}
                        onChange={(e, newValue) => {
                            setIsErrors(false);
                            setErrorList((prev) => ({ ...prev, [field.name]: false }));
                            setAiSuggestedFields((prev) => prev.filter((f) => f !== field.name));
                            if (inputAssetName === asset) {
                                setAssetRecord((prev) => ({
                                    ...prev,
                                    [field.name]: newValue,
                                }));
                            } else {
                                setInspectionRecord((prev) => ({
                                    ...prev,
                                    [field.name]: newValue,
                                }));
                            }
                        }}
                        onInputChange={(e, newInputValue) => {
                            setIsErrors(false);
                            setErrorList((prev) => ({ ...prev, [field.name]: false }));
                            setAiSuggestedFields((prev) => prev.filter((f) => f !== field.name));
                            if (inputAssetName === asset) {
                                setAssetRecord((prev) => ({
                                    ...prev,
                                    [field.name]: newInputValue,
                                }));
                            } else {
                                setInspectionRecord((prev) => ({
                                    ...prev,
                                    [field.name]: newInputValue,
                                }));
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={field.displayName}
                                variant="outlined"
                                size="small"
                                {...sharedProps}
                                InputLabelProps={{ style: { color: "#4285f4" } }}
                                InputProps={{
                                    ...params.InputProps,
                                    style: { ...params.InputProps?.style, ...aiHighlightStyle },
                                }}
                                inputProps={{
                                    ...params.inputProps,
                                    style: {
                                        height: "23px",
                                        paddingLeft: "12px",
                                    },
                                }}
                            />
                        )}
                    />
                );

            case "textarea":
                return (
                    <TextField
                        label={field.displayName}
                        autoComplete="off"
                        variant="outlined"
                        fullWidth
                        size="small"
                        {...sharedProps}
                        InputLabelProps={{ style: { color: "#4285f4" } }}
                        InputProps={{ style: { height: "100px", ...aiHighlightStyle } }}
                        multiline
                        name={field.name}
                        type="text"
                        value={value || ""}
                        onChange={(e) => handleChangeValue(e, field.name, inputAssetName)}
                    />
                );

            case "number":
                return (
                    <TextField
                        label={field.displayName}
                        autoComplete="off"
                        variant="outlined"
                        fullWidth
                        size="small"
                        {...sharedProps}
                        InputLabelProps={{ style: { color: "#4285f4" } }}
                        InputProps={{
                            style: { marginBottom: "10px", ...aiHighlightStyle },
                            inputProps: { min: 0, step: 0.01 },
                        }}
                        type="number"
                        value={value !== undefined && value !== null ? value : ""}
                        onChange={(e) => {
                            setIsErrors(false);
                            setErrorList((prev) => ({ ...prev, [field.name]: false }));
                            setAiSuggestedFields((prev) => prev.filter((f) => f !== field.name));
                            const floatVal = parseFloat(e.target.value);
                            if (inputAssetName === asset) {
                                setAssetRecord((prev) => ({ ...prev, [field.name]: floatVal }));
                            } else {
                                setInspectionRecord((prev) => ({
                                    ...prev,
                                    [field.name]: floatVal,
                                }));
                            }
                        }}
                    />
                );

            case "date":
                return (
                    <TextField
                        autoComplete="off"
                        label={field.displayName}
                        variant="outlined"
                        fullWidth
                        size="small"
                        {...sharedProps}
                        InputLabelProps={{ style: { color: "#4285f4" }, shrink: true }}
                        InputProps={{
                            style: { marginBottom: "10px", ...aiHighlightStyle },
                        }}
                        value={value ? formatDataDate(value) : ""}
                        type="date"
                        onChange={(e) => handleChangeValue(e, field.name, inputAssetName)}
                    />
                );

            default:
                // text
                return (
                    <TextField
                        label={field.displayName}
                        autoComplete="off"
                        variant="outlined"
                        fullWidth
                        size="small"
                        {...sharedProps}
                        InputLabelProps={{ style: { color: "#4285f4" } }}
                        InputProps={{
                            style: { marginBottom: "10px", ...aiHighlightStyle },
                        }}
                        name={field.name}
                        type="text"
                        value={value || ""}
                        onChange={(e) => handleChangeValue(e, field.name, inputAssetName)}
                    />
                );
        }
    };

    const renderExistingFiles = (record, inputAssetName) => {
        if (!record || !record.assetFiles || !record.assetFiles.length) return null;
        return (
            <div style={{ paddingTop: "12px" }}>
                {record.assetFiles.map((fileObj, idx) => {
                    const isRemoving = removeFileArray.includes(fileObj.AttachmentID);
                    const isImage =
                        fileObj.AttachmentName.includes(".jpeg") ||
                        fileObj.AttachmentName.includes(".jpg") ||
                        fileObj.AttachmentName.includes(".png");

                    return isRemoving ? (
                        <div
                            key={fileObj.AttachmentID}
                            style={{ background: "#00000036", borderRadius: "6px", marginBottom: "8px" }}
                        >
                            <div style={{ fontWeight: 600, textAlign: "center" }}>
                                Are you sure you want to remove?
                            </div>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                <span
                                    className="spanDeleteFile"
                                    style={{ color: "green", paddingRight: "20px" }}
                                    onClick={() => deleteAzureFile(fileObj.AttachmentID)}
                                >
                                    Yes
                                </span>
                                <span
                                    className="spanDeleteFile"
                                    style={{ color: "red", paddingLeft: "20px" }}
                                    onClick={() => cancelRemoveFile(fileObj.AttachmentID)}
                                >
                                    No
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div
                            key={fileObj.AttachmentID}
                            style={{ display: "flex", justifyContent: "space-between" }}
                        >
                            <div className="filePill">
                                <div style={{ display: "flex" }}>
                                    <InsertDriveFileOutlinedIcon
                                        style={{
                                            color: "white",
                                            marginBottom: "auto",
                                            marginTop: "auto",
                                        }}
                                    />
                                    <div style={{ paddingLeft: "8px" }}>
                                        <div>
                                            <b style={{ fontWeight: 600 }} />
                                            {isImage
                                                ? `Image ${idx + 1}`
                                                : `File ${idx + 1}`}
                                        </div>
                                    </div>
                                </div>
                                <CloseIcon
                                    color="secondary"
                                    style={{
                                        marginTop: "auto",
                                        marginBottom: "auto",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => setRemoveFileFlag(fileObj.AttachmentID)}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Renders textual fields from a read-only perspective
    const renderTextFields = (fields, record) => {
        if (!fields) return null;
        return (
            <div>
                {fields.map(
                    (f) =>
                        f.display && (
                            <p className="markerElements" key={f.name}>
                                <b style={{ color: "#4285f4" }}>{f.displayName}: </b>
                                <span>
                                    {renderReadOnlyValue(record[f.name], f.name)}
                                </span>
                            </p>
                        )
                )}
                {record.assetFiles.map((x, fileIndex) => (
                    <div
                        key={x.AttachmentID}
                        style={{ display: "flex", justifyContent: "space-between" }}
                    >
                        <div
                            className="displayFilePill"
                            onClick={() => getFile(x.StorageLocation)}
                        >
                            <div>
                                <div>
                                    {x.AttachmentName.includes(".jpeg") ||
                                        x.AttachmentName.includes(".jpg") ||
                                        x.AttachmentName.includes(".png")
                                        ? `Image ${fileIndex + 1}`
                                        : `File ${fileIndex + 1}`}
                                </div>
                            </div>
                            <div style={{ marginTop: "auto", marginBottom: "auto" }}>
                                <InsertDriveFileOutlinedIcon style={{ color: "white" }} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderReadOnlyValue = (val, fieldName) => {
        if (fieldName.toLowerCase().includes("date") && val) {
            return moment.utc(val).format("MM-DD-YYYY");
        } else if (typeof val === "boolean") {
            return val ? "Yes" : "No";
        } else if (fieldName && (fieldName.toLowerCase().includes('latitude') || fieldName.toLowerCase().includes('longitude'))) {
            // Format coordinates to 5 decimal places for display
            return formatCoordinateForDisplay(val, fieldName);
        }
        return val || "";
    };

    // --------------
    //  FILE REMOVAL
    // --------------
    const setRemoveFileFlag = (attachmentId) => {
        setRemoveFileArray((prev) => [...prev, attachmentId]);
    };
    const cancelRemoveFile = (attachmentId) => {
        setRemoveFileArray((prev) => prev.filter((x) => x !== attachmentId));
    };

    const removeFile = (id) => {
        //check to remove from database or local
        var files = inputFiles.filter(x => x.id !== id)
        var inspectionFiles = inspectionInputFiles.filter(x => x.id !== id)
        setRemoveFileArray((prev) => prev.filter((x) => x !== id));
        setInputFiles(files);
        setInspectionInputFiles(inspectionFiles);
    }

    const deleteAzureFile = async (attachmentId) => {
        try {
            await axios.get(`/api/deleteFile/${attachmentId}`);
            // remove from assetRecord
            setAssetRecord((prev) => ({
                ...prev,
                assetFiles: prev.assetFiles.filter((f) => f.AttachmentID !== attachmentId),
            }));
            refreshDataWithOutClosing();
        } catch (err) {
            console.error("Error deleting azure file:", err);
        }
    };

    // --------------
    //  MAP MOVEMENT
    // --------------
    /**
     * Duplicate the current asset: clone user-editable fields, auto-increment
     * the Client ID, and enter map placement mode for the new copy.
     */
    const handleDuplicate = () => {
        // 1. Clone assetRecord, stripping system fields and location fields
        // Location fields are excluded so reverse geocoding can populate them for the new location
        const duplicatedRecord = {};
        for (const [key, value] of Object.entries(assetRecord)) {
            if (!SYSTEM_FIELDS.has(key) && !LOCATION_FIELDS.has(key)) {
                duplicatedRecord[key] = value;
            }
        }

        // 2. Auto-increment the Client ID
        const nextId = getNextAssetId(serviceData, asset);
        if (nextId && nextId.suggestedId) {
            duplicatedRecord[nextId.fieldName] = nextId.suggestedId;
        }

        // 3. Clear markerData in parent (App.js) so CustomMap knows we're in "add new" mode, not edit mode
        // This ensures isEditingMarker becomes false in CustomMap, allowing map clicks to work
        if (onClearMarkerData) {
            onClearMarkerData();
        }

        // 4. Switch to "add new" mode with pre-filled data
        setAssetRecord(duplicatedRecord);
        setIsEdit(false);
        setNotEditingInfo(true);
        setSelectedAsset(true);

        // 5. Enter map placement mode (crosshair cursor) - force enable (don't toggle)
        // This ensures showFormAndAddMarker is set to true, matching the navbar "Add Asset" flow
        addMarkerToMap(asset, true);
    };

    const move = (key) => {
        setIsMoving((prev) => !prev);
        if (!isMoving) {
            // Start move
            moveMarker(key);
        } else {
            // Cancel move => revert
            const revertKey = {
                ...key,
                Latitude: originalLatLng.lat,
                Longitude: originalLatLng.lng,
            };
            moveMarker(revertKey, false);
        }
    };

    const onSubmitMove = async () => {
        try {
            const currentUser = userData.user || user.Created_User || user.email || 'Unknown User';
            const currentDate = new Date();
            
            console.log('Moving asset with user data:', {
                user: currentUser,
                date: currentDate
            });
            
            const tempData = {
                ...assetRecord,
                Latitude: LatLng.lat,
                Longitude: LatLng.lng,
                Last_Editor: currentUser,
                Last_Edited_Date: currentDate,
            };
            const inputObject = {
                asset,
                values: tempData,
                types: inputObjectArray,
            };
            await axios.post("/api/assetUPD", inputObject);
            setIsMoving(false);
            await refreshDataWithOutClosing();
        } catch (err) {
            console.error("Error updating location:", err);
        }
    };

    // --------------
    // SUBMIT LOGIC
    // --------------
    const onSubmit = async (e, inputAssetName) => {
        e.preventDefault();
        setIsErrors(false);
        setIsSubmitting(true);

        // Upload files if existing asset
        if (inputFiles.length && assetRecord[`${asset}ID`]) {
            await uploadFiles(
                inputFiles,
                `${asset}/${assetRecord[`${asset}ID`]}`,
                onShowError
            );
        }

        // Check required fields
        const reqFields = inputObjectArray.filter((f) => f.required);
        let hadError = false;
        let tempErrorList = { ...errorList };
        for (const field of reqFields) {
            if (!assetRecord[field.name]) {
                hadError = true;
                tempErrorList[field.name] = true;
            }
        }
        setErrorList(tempErrorList);
        if (hadError) {
            setIsErrors(true);
            setIsSubmitting(false);
            return;
        }

        try {
            if (isEdit) {
                // Update existing
                const currentUser = userData.user || user.Created_User || user.email || 'Unknown User';
                const currentDate = new Date();
                
                console.log('Updating asset with user data:', {
                    user: currentUser,
                    date: currentDate
                });
                
                const inputObject = {
                    asset: inputAssetName,
                    values: {
                        ...assetRecord,
                        Last_Editor: currentUser,
                        Last_Edited_Date: currentDate,
                    },
                    types: inputObjectArray,
                };
                await axios.post("/api/assetUPD", inputObject);
                refreshDataWithOutClosing();
                setNotEditingInfo(true);
            } else {
                // Insert new
                const { data: guidRes } = await axios.get("/api/newGUID");
                const GlobalId = Object.values(guidRes.result.flat()[0])[0];
                
                // Ensure user data is available
                const currentUser = userData.user || user.Created_User || user.email || 'Unknown User';
                const currentDate = new Date();
                
                console.log('Creating asset with user data:', {
                    user: currentUser,
                    date: currentDate,
                    ClientID: userData.ClientID
                });
                
                const inputObject = {
                    asset: inputAssetName,
                    values: {
                        ...assetRecord,
                        Created_User: currentUser,
                        Created_Date: currentDate,
                        Last_Editor: currentUser,
                        Last_Edited_Date: currentDate,
                        ClientID: userData.ClientID,
                        GlobalID: GlobalId,
                        DeleteFacility: "No",
                    },
                    types: inputObjectArray,
                };
                setAssetRecord((prev) => ({ ...prev, GlobalID: GlobalId }));

                const insertRes = await axios.post("/api/assetInsert", inputObject);
                const assetId = insertRes.data.result.recordset[0][`${asset}ID`];
                // Upload files for new asset
                if (inputFiles.length) {
                    await uploadFiles(
                        inputFiles,
                        `${asset}/${assetId}`,
                        onShowError
                    );
                }
            }
            // Refresh & reset
            await refreshData();
            setAssetRecord({});
            setLatlngError(false);
            setIsSubmitting(false);
        } catch (err) {
            console.error("Error saving asset:", err);
            setIsSubmitting(false);
        }
    };

    const uploadFiles = async (fileArray, endpoint, handleError) => {
        const formData = new FormData();
        let fileData = {};
        fileArray.forEach((f, i) => {
            formData.append(`file${i}`, f.file);
            fileData[`file${i}`] = {
                fileName: f.fileName,
                fileDescription: f.fileDescription,
            };
        });
        formData.append("fileData", JSON.stringify(fileData));
        try {
            await axios.post(`/api/uploadAttachment/${endpoint}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
        } catch (uploadErr) {
            console.error("File upload error:", uploadErr.response.data);
            if (handleError) {
                handleError(JSON.stringify(uploadErr.response.data.error, null, 2));
            }
        }
    };

    // Handle step to fill out line or lat/lng
    const SendToBack = async (inputObj) => {
        if (isLineAsset) {
            if (!addedLinesLatLng.length) {
                setLatlngError(true);
                return;
            }
            // We want to store the line JSON
            setAssetRecord((prev) => ({
                ...prev,
                LineJSON: JSON.stringify(addedLinesLatLng),
            }));
            setPickLocation(true);
            CallFromAddToMapToCustomMap(true);
        } else {
            if (!LatLng.lat && !LatLng.lng) {
                setLatlngError(true);
                return;
            }
            setAssetRecord((prev) => ({
                ...prev,
                Latitude: LatLng.lat,
                Longitude: LatLng.lng,
            }));
            setPickLocation(true);
        }
    };

    // --------------
    // INSPECTION
    // --------------
    const addInspection = () => {
        setSelectInspection(true);
        setIsEditingInspection(false);
        setInspectionRecord({});
    };

    const getInspections = () => {
        if (!inspections.length) {
            return <div style={{ padding: "10px 5px", margin: "auto" }}>No Inspections</div>;
        }
        return (
            <div style={{ padding: "10px 5px", margin: "auto", display: "flex", flexDirection: "column" }}>
                {inspections.map((ins, idx) => (
                    <div style={{ marginBottom: "5px" }} key={idx}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => editInspection(ins)}
                        >
                            {moment.utc(ins.InspectionDate).format("MM/DD/YYYY")}
                        </Button>
                    </div>
                ))}
            </div>
        );
    };

    const editInspection = (inspection) => {
        setSelectInspection(true);
        setIsEditingInspection(true);
        setInspectionRecord(inspection);
    };

    const goToInspectionList = () => {
        setSelectInspection(false);
        setIsEditingInspection(false);
        setNotEditingInspection(true);
        setInspectionRecord({});
    };

    const onInspectionSubmit = async (e, inputAssetName) => {
        e.preventDefault();
        setIsSubmitting(true);
        setIsErrors(false);

        // Check required fields
        const reqFields = inspectionObjectArray.filter((x) => x.required);
        let hadError = false;
        let tempErrList = { ...errorList };
        for (const rf of reqFields) {
            if (!inspectionRecord[rf.name]) {
                hadError = true;
                tempErrList[rf.name] = true;
            }
        }
        setErrorList(tempErrList);
        if (hadError) {
            setIsErrors(true);
            setIsSubmitting(false);
            return;
        }

        // Upload any attached files
        if (inspectionInputFiles.length) {
            await uploadFiles(
                inspectionInputFiles,
                `${asset}Inspection/${data[`${asset}ID`]}`,
                onShowError
            );
            setTempFile([]);
        }

        try {
            if (isEditingInspection) {
                // Update existing inspection
                const currentUser = userData.user || user.Created_User || user.email || 'Unknown User';
                const currentDate = new Date();
                
                console.log('Updating inspection with user data:', {
                    user: currentUser,
                    date: currentDate
                });
                
                const inputObj = {
                    asset: inputAssetName,
                    values: {
                        ...inspectionRecord,
                        Last_Editor: currentUser,
                        Last_Edited_Date: currentDate,
                    },
                    types: inspectionObjectArray,
                };
                await axios.post("/api/assetInspectionUPD", inputObj);

                // Refresh the list
                await refreshInspectionList(data[`${asset}ID`]);
                setSelectInspection(false);
                setIsEditingInspection(false);
                setInspectionInputFiles([]);
                setInspectionRecord({});
            } else {
                // Insert new inspection
                const currentUser = userData.user || user.Created_User || user.email || 'Unknown User';
                const currentDate = new Date();
                
                console.log('Creating inspection with user data:', {
                    user: currentUser,
                    date: currentDate
                });
                
                const inputObj = {
                    asset: inputAssetName,
                    values: {
                        ...inspectionRecord,
                        [`${asset}ID`]: assetRecord[`${asset}ID`],
                        GlobalID: assetRecord.GlobalID,
                        Created_User: currentUser,
                        Created_Date: currentDate,
                        Last_Editor: currentUser,
                        Last_Edited_Date: currentDate,
                        ClientID: userData.ClientID,
                        DeleteFacility: "No",
                    },
                    types: inspectionObjectArray,
                };
                await axios.post("/api/assetInspectionInsert", inputObj);
                // Refresh inspection list
                await refreshInspectionList(data[`${asset}ID`]);

                // Update days since last inspection
                const inspectionDate = inspectionRecord.InspectionDate;
                const daysBetween = Math.floor(
                    (new Date() - new Date(inspectionDate)) / (1000 * 60 * 60 * 24)
                );
                const updatedServiceData = serviceData.map((svc) => {
                    if (svc.GlobalID === assetRecord.GlobalID) {
                        return { ...svc, DaysSinceLastInspection: daysBetween };
                    }
                    return svc;
                });
                removeHightLightForMarker(assetRecord.GlobalID, updatedServiceData);
            }
        } catch (err) {
            console.error("Error saving inspection:", err);
            if (err.response.data.error) {
                onShowError(JSON.stringify(err.response.data.error, null, 2));
            }
        }

        setIsSubmitting(false);
        setIsEditingInspection(false);
        setNotEditingInspection(true);
        goToInspectionList();
        setInspectionInputFiles([]);

    };

    const refreshInspectionList = async (theId) => {
        if (!theId) return;
        const refreshObject = {
            asset: `${asset}Inspection`,
            values: {
                [`${asset}ID`]: theId,
                ClientID: userData.ClientID,
            },
            types: inspectionObjectArray.filter(
                (x) => x.name === `${asset}ID` || x.name === "ClientID"
            ),
        };
        try {
            const { data: resp } = await axios.post("/api/assetInspectionSEL", refreshObject);
            setInspections(resp.result[0] || []);
            setInspectionUniqueNames(resp.uniqueNames[0] || []);
        } catch (err) {
            console.error("Error refreshing inspection list:", err);
        }
    };

    // ---------------
    //  FILE HANDLERS
    // ---------------
    const renderFileUpload = (inspection = false) => {
        return (
            <div style={{ paddingTop: "10px", paddingBottom: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-evenly" }}>
                    <div>
                        <input
                            style={{ display: "none" }}
                            id="temp-contained-button-file"
                            multiple
                            type="file"
                            onChange={(e) => grabFile(e, inspection)}
                        />
                        <label htmlFor="temp-contained-button-file">
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={tempFile.length !== 0}
                                component="span"
                                style={{
                                    fontSize: "10px",
                                    paddingLeft: "8px",
                                    paddingRight: "8px",
                                }}
                            >
                                <InsertDriveFileOutlinedIcon style={{ fontSize: "18px" }} />
                                <span style={{ paddingLeft: "6px" }}>Select File</span>
                            </Button>
                        </label>
                    </div>
                </div>
            </div>
        );
    };

    const grabFile = (e, inspection) => {
        const { id } = e.target;
        if (e.target.files && e.target.files.length) {
            const index = tempFile.length;
            const newFile = {
                type: id,
                id: `${id}${index}`,
                file: e.target.files[0],
            };
            const updatedTemp = [...tempFile, newFile];
            setTempFile(updatedTemp);
            FileUploadStep2(inspection, updatedTemp);
            setTimeout(() => {
                const pane = document.getElementById("mapFormTab1");
                if (pane) pane.scrollTop = 1000;
            }, 300);
        }
    };

    const FileUploadStep2 = (inspection, tempFiles) => {
        // We only handle the first item? The code suggests so
        if (!tempFiles.length) return;
        const fileObj = tempFiles[0];
        const fileType = fileObj.file.name.split(".").pop();
        const guid = crypto.randomUUID();
        let tempAssetId = assetRecord[`${asset}ID`];
        if (!tempAssetId) {
            // Make the TempAssetId from the length of the serviceData array filtered by [`${asset}ID`]
            tempAssetId = serviceData.length;
        }
        const tempFileName = `${tempAssetId}_${userData.ClientID}_${guid}`;

        // Build the new file object
        const newInputFile = {
            ...fileObj,
            fileName: `${tempFileName}.${fileType}`,
            fileDescription: "",
            AttachmentName: `${tempFileName}.${fileType}`,
            AttachmentDescription: "",
        };

        if (inspection) {
            setInspectionInputFiles((prev) => [...prev, newInputFile]);
        } else {
            setInputFiles((prev) => [...prev, newInputFile]);
        }
        // Clear tempFile
        setTempFile([]);
    };

    const cameraPic = (imageBlob) => {
        // Convert blob to File
        const file = new File([imageBlob], "capture.jpg", { type: "image/jpeg" });
        const newFileObj = {
            type: "capturedImage",
            id: "capturedImage0",
            file,
        };
        const updatedTemp = [...tempFile, newFileObj];
        setTempFile(updatedTemp);
        setShowWebcam(false);

        FileUploadStep2(selectInspection, updatedTemp);

        setTimeout(() => {
            const pane = document.getElementById("mapFormTab1");
            if (pane) pane.scrollTop = 1000;
        }, 300);
    };

    const cancelPicFn = () => {
        const video = document.getElementsByTagName("video")[0];
        if (video) {
            video.pause();
            if (video.srcObject) {
                const track = video.srcObject.getVideoTracks()[0];
                if (track) track.stop();
            }
        }
        setShowWebcam(false);
    };

    const getFile = async (storageLocation) => {
        const body = { path: storageLocation };
        try {
            const { data: result } = await axios.post("/api/getFile", body);
            setFileData(result.fileData);
            setFileMime(storageLocation);
        } catch (err) {
            console.error("Error fetching file:", err);
        }
    };

    // --------------
    // DELETE MARKER
    // --------------
    const deleteMarkerFunction = () => {
        if (markerFormsProps.deleteMarker) {
            markerFormsProps.deleteMarker(assetRecord);
        } else {
            deleteMarker(assetRecord);
        }
    };

    // --------------
    // RENDER
    // --------------
    if (!selectedAsset && !isEdit) {
        // Step 1: pick an asset (unless we're editing)
        return (
            <div className="AddToMapContainer">
                <div
                    style={{
                        position: "absolute",
                        right: "10px",
                        top: "10px",
                        zIndex: 1,
                    }}
                >
                    <i
                        className="fa fa-times"
                        style={{ color: "red", fontSize: "27px", cursor: "pointer" }}
                        onClick={cancel}
                        aria-hidden
                    />
                </div>
                <div className="outerContainer">
                    <h2 className="containerHeader">Select an Asset To Add</h2>
                    {/* Example asset button */}
                    <div className="assetRow">
                        <Button
                            variant="contained"
                            style={{ padding: "10px 27px 10px 25px", width: "170px" }}
                            onClick={() => handleAddAsset("Hydrant")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <img
                                    src={hydrantIcon}
                                    alt="Hydrant"
                                    style={{ width: "20px", height: "20px" }}
                                />
                                <span style={{ paddingLeft: "20px", margin: "auto" }}>
                                    <b>Hydrant</b>
                                </span>
                            </div>
                        </Button>
                    </div>

                    <div className="assetRow">
                        <Button
                            variant="contained"
                            style={{ padding: "10px 27px", width: "170px" }}
                            onClick={() => handleAddAsset("Manhole")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <img
                                    src={manholeIcon}
                                    alt="Manhole"
                                    style={{ width: "20px", height: "20px" }}
                                />
                                <span style={{ paddingLeft: "20px", margin: "auto" }}>
                                    <b>Manhole</b>
                                </span>
                            </div>
                        </Button>
                    </div>

                    <div className="assetRow">
                        <Button
                            variant="contained"
                            style={{ padding: "10px 27px", width: "170px" }}
                            onClick={() => handleAddAsset("Valve")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <img
                                    src={valveIcon}
                                    alt="Valve"
                                    style={{ width: "20px", height: "20px" }}
                                />
                                <span style={{ paddingLeft: "20px", margin: "auto" }}>
                                    <b>Valve</b>
                                </span>
                            </div>
                        </Button>
                    </div>

                    <div className="assetRow">
                        <Button
                            variant="contained"
                            style={{ padding: "10px 27px", width: "170px" }}
                            onClick={() => handleAddAsset("TrafficSign")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <img
                                    src={trafficSignIcon}
                                    alt="TrafficSign"
                                    style={{ width: "20px", height: "20px" }}
                                />
                                <span style={{ paddingLeft: "20px", margin: "auto" }}>
                                    <b>Signs</b>
                                </span>
                            </div>
                        </Button>
                    </div>

                    <div className="assetRow">
                        <Button
                            variant="contained"
                            style={{ padding: "11px 10px", width: "170px" }}
                            onClick={() => handleAddAsset("WaterMeter")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <img
                                    src={waterMeterIcon}
                                    alt="WaterMeter"
                                    style={{ width: "20px", height: "20px" }}
                                />
                                <span style={{ paddingLeft: "9px", margin: "auto", fontSize: "13px" }}>
                                    <b>Water Meters</b>
                                </span>
                            </div>
                        </Button>
                    </div>

                    <div className="assetRow">
                        <Button
                            variant="contained"
                            style={{ padding: "11px 13px", width: "170px" }}
                            onClick={() => handleAddAsset("WaterLine")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <img
                                    src={waterlineIcon}
                                    alt="WaterLine"
                                    style={{ width: "20px", height: "20px" }}
                                />
                                <span style={{ paddingLeft: "20px", margin: "auto" }}>
                                    <b>Water Line</b>
                                </span>
                            </div>
                        </Button>
                    </div>

                    <div className="assetRow">
                        <Button
                            variant="contained"
                            style={{ padding: "11px 13px", width: "170px" }}
                            onClick={() => handleAddAsset("SewerLine")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <img
                                    src={sewerlineIcon}
                                    alt="SewerLine"
                                    style={{ width: "20px", height: "20px" }}
                                />
                                <span style={{ paddingLeft: "20px", margin: "auto" }}>
                                    <b>Sewer Line</b>
                                </span>
                            </div>
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const deleteFile = async (id) => {
        await axios.get(`/api/deleteFile/${id}`)
            .then((result) => {
                // refresh hard part
                // var newAssetFiles = this.state[this.state.asset].assetFiles.filter(x => x.AttachmentID !== id)

                // this.setState({
                //     [this.state.asset]: {
                //         ...this.state[this.state.asset],
                //         assetFiles: newAssetFiles
                //     }
                // })
                // this.props.refreshDataWithOutClosing()

                setAssetRecord((prev) => ({
                    ...prev,
                    assetFiles: prev.assetFiles.filter((f) => f.AttachmentID !== id),
                }));
                refreshDataWithOutClosing();
            })
            .catch(error => console.log('error', error))
    }

    // If user is editing or has selected an asset
    return (
        <div className="AddToMapContainer">
            {!isMoving && (
                <div
                    style={{
                        position: "absolute",
                        right: "10px",
                        top: "10px",
                        zIndex: 1,
                    }}
                >
                    <i
                        className="fa fa-times"
                        style={{ color: "red", fontSize: "27px", cursor: "pointer" }}
                        onClick={() => {
                            setAssetRecord({});
                            cancel();
                        }}
                        aria-hidden
                    />
                </div>
            )}

            {showWebcam && (
                <FullScreenMobileView
                    cameraPic={cameraPic}
                    cancelPic={cancelPicFn}
                />
            )}

            {fileData && (
                <FileViewDownload
                    imageData={fileData}
                    fileMime={fileMime}
                    cancelPic={() => setFileData(null)}
                />
            )}

            <div className="inputContainer">
                <Nav tabs>
                    <NavItem>
                        <NavLink
                            active={activeTab === "1"}
                            onClick={() => setActiveTab("1")}
                            style={{ cursor: "pointer" }}
                        >
                            <div style={{ display: "flex" }}>
                                <b>Info</b>
                            </div>
                        </NavLink>
                    </NavItem>

                    {isEdit && !asset.includes("WaterMeter") && (
                        <NavItem>
                            <NavLink
                                active={activeTab === "2"}
                                onClick={() => setActiveTab("2")}
                                style={{ cursor: "pointer" }}
                            >
                                Inspections
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>

                <TabContent activeTab={activeTab} className="contentTab2" id="mapFormTab1">
                    <TabPane tabId="1">
                        {isMoving ? (
                            <div style={{ minWidth: "225px" }}>
                                <p style={{ textAlign: "center" }}>
                                    <b>
                                        Drag the asset to the new location and Press Submit.
                                    </b>
                                </p>
                                <div style={{ display: "flex", justifyContent: "space-evenly" }}>
                                    <Button
                                        color="primary"
                                        className="markerButton"
                                        size="sm"
                                        onClick={onSubmitMove}
                                    >
                                        Submit
                                    </Button>
                                    <Button
                                        color="error"
                                        className="markerButton"
                                        size="sm"
                                        onClick={() => move(assetRecord)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : notEditingInfo && !selectedAsset && isEdit ? (
                            <div>
                                {inputObjectArray && renderTextFields(inputObjectArray, assetRecord)}

                                {/* Read-only mode indicator */}
                                {isReadOnly && (
                                    <div style={{
                                        padding: "12px",
                                        margin: "10px 0",
                                        backgroundColor: "#fff3e0",
                                        borderRadius: "4px",
                                        textAlign: "center",
                                        color: "#e65100",
                                        fontSize: "14px"
                                    }}>
                                        🔒 View Only Mode - Editing is disabled
                                    </div>
                                )}

                                <div className="buttonBar">
                                    {canWrite && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className="ButtonShadow"
                                            onClick={() => setNotEditingInfo(false)}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    {canWrite && !isLineAsset && (
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            className="ButtonShadow"
                                            onClick={() => move(assetRecord)}
                                        >
                                            Move
                                        </Button>
                                    )}
                                    {canWrite && (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            className="ButtonShadow"
                                            onClick={handleDuplicate}
                                        >
                                            Duplicate
                                        </Button>
                                    )}
                                    {canDelete && (
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            className="ButtonShadow"
                                            onClick={deleteMarkerFunction}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : !pickLocation && !isEdit ? (
                            // If adding a new asset, but haven't placed location or line
                            isLineAsset ? (
                                <div>
                                    <form>
                                        <p
                                            style={{
                                                padding: "7px 27px",
                                                textAlign: "center",
                                                fontWeight: 800,
                                                fontSize: "15px",
                                            }}
                                        >
                                            Click points on the map to draw the line asset and Press
                                            Next.
                                        </p>
                                        <br />
                                        <p style={{ textAlign: "center" }}>
                                            <b>
                                                You can click the Location icon to zoom to your location.
                                                You will be unable to edit once you complete
                                            </b>
                                        </p>
                                        {console.log("addedLinesLatLng", addedLinesLatLng)}
                                        {addedLinesLatLng && addedLinesLatLng.length !== 0 && (
                                            <div style={{ display: "flex", justifyContent: "center", padding: "10px" }}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <p style={{ fontWeight: 800, fontSize: "15px" }}>
                                                        Added Points
                                                    </p>
                                                    {addedLinesLatLng.map((xy, idx) => (
                                                        <div
                                                            key={idx}
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                width: "225px",
                                                                background: "#0c0cb9",
                                                                color: "white",
                                                                padding: "5px 14px",
                                                                borderRadius: "15px",
                                                                marginBottom: "6px",
                                                            }}
                                                        >
                                                            <div>
                                                                <p>{`Lat: ${formatCoordinateForDisplay(xy[0], 'latitude')}`}</p>
                                                                <p>{`Lng: ${formatCoordinateForDisplay(xy[1], 'longitude')}`}</p>
                                                            </div>
                                                            <div style={{ margin: "19px 4px" }}>
                                                                <img
                                                                    src={trashcan}
                                                                    style={{ width: "20px", height: "20px", cursor: "pointer" }}
                                                                    alt="Trash"
                                                                    onClick={() => removeLatLngLines(idx)}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <div className="buttonBar">
                                            <Button
                                                variant="contained"
                                                color="error"
                                                className="ButtonShadow"
                                                onClick={() => clearLinesFunc(true)}
                                            >
                                                Clear
                                            </Button>
                                            {addedLinesLatLng.length !== 0 && (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    className="ButtonShadow"
                                                    onClick={() => SendToBack({})}
                                                >
                                                    Complete
                                                </Button>
                                            )}
                                        </div>
                                    </form>
                                </div>
                            ) : (
                                // Not line => point asset
                                <div>
                                    <form>
                                        <p
                                            style={{
                                                padding: "29px",
                                                textAlign: "center",
                                                fontWeight: 800,
                                                fontSize: "15px",
                                            }}
                                        >
                                            Click point on the map to where the asset is located and Press Next.
                                        </p>
                                        <br />
                                        <p style={{ textAlign: "center" }}>
                                            <b>
                                                You can click the Location icon to zoom to your location.
                                            </b>
                                        </p>
                                        {latlngError && (
                                            <p
                                                style={{
                                                    textAlign: "center",
                                                    fontWeight: 800,
                                                    fontSize: "15px",
                                                    color: "red",
                                                }}
                                            >
                                                Click a point on the map to add.
                                            </p>
                                        )}
                                        <div className="buttonBar">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className="ButtonShadow"
                                                onClick={() => SendToBack({})}
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            )
                        ) : pickLocation && !isEdit ? (
                            // Now fill out the actual form for the new asset
                            <form>
                                <VoiceInput
                                    inputObjectArray={inputObjectArray}
                                    previousValues={serviceData}
                                    assetType={asset}
                                    onFieldsMatched={(fields) =>
                                        setAssetRecord((prev) => ({ ...prev, ...fields }))
                                    }
                                >
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={aiAssistLoading ? <CircularProgress size={14} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
                                        onClick={handleAIAssist}
                                        disabled={aiAssistLoading}
                                        sx={{
                                            textTransform: "none",
                                            fontSize: "12px",
                                            padding: "3px 10px",
                                            borderColor: "#3f51b5",
                                            color: "#3f51b5",
                                            whiteSpace: "nowrap",
                                            "&:hover": {
                                                borderColor: "#3f51b5",
                                                backgroundColor: "rgba(63,81,181,0.08)",
                                            },
                                        }}
                                    >
                                        {aiAssistLoading ? "Suggesting…" : "AI Assist"}
                                    </Button>
                                </VoiceInput>
                                {inputObjectArray && renderForm(inputObjectArray, asset, serviceData)}
                                {inputFiles.length !== 0
                                    && inputFiles.map((x, fileIndex) => removeFileArray.includes(x.id)
                                        ? <div style={{ background: '#00000036', borderRadius: '6px' }}>
                                            <div style={{ fontWeight: '600', textAlign: 'center' }}>Are you sure you want to remove?</div>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <span className='spanDeleteFile' style={{ color: 'green', paddingRight: '20px' }} onClick={() => removeFile(x.id)}>Yes</span>
                                                <span className='spanDeleteFile' style={{ color: 'red', paddingLeft: '20px' }} onClick={() => cancelRemoveFile(x.id)}>No</span>
                                            </div>
                                        </div>
                                        : <div key={x.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div className='filePill' style={{ marginBottom: '0px', marginTop: '10px' }}>
                                                <div style={{ display: 'flex' }}>
                                                    <InsertDriveFileOutlinedIcon style={{ color: "white", marginBottom: 'auto', marginTop: 'auto' }} />
                                                    <div style={{ paddingLeft: '8px' }}>
                                                        <div><b style={{ fontWeight: 600 }}></b>{x.AttachmentName.includes('.jpeg') || x.AttachmentName.includes('.jpg') || x.AttachmentName.includes('.png') ? `Image to be added ${fileIndex + 1}` : `File to be added ${fileIndex + 1}`}</div>
                                                        {/* <div><b style={{ fontWeight: 600 }}>Name: </b>{x.AttachmentName}</div> */}
                                                        {/* <div><b style={{ fontWeight: 600 }}>Description: </b>{x.AttachmentDescription}</div> */}
                                                    </div>
                                                </div>
                                                <CloseIcon color="secondary" style={{ marginTop: 'auto', marginBottom: 'auto', cursor: 'pointer' }} onClick={() => setRemoveFileFlag(x.id)} />
                                            </div>
                                        </div>)
                                }
                                {renderFileUpload()}
                                <div className="buttonBar">
                                    {isSubmitting ? (
                                        <div style={{ display: "flex", justifyContent: "center" }}>
                                            <CircularProgress />
                                        </div>
                                    ) : (
                                        <div>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className="ButtonShadow"
                                                disabled={isSubmitting}
                                                onClick={(e) => onSubmit(e, asset)}
                                            >
                                                Submit
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {isErrors && <div className="errorBarMessage">There are errors in the Form!</div>}
                            </form>
                        ) : (
                            // Edit existing or final form for existing
                            <form>
                                {!notEditingInfo && (
                                    <VoiceInput
                                        inputObjectArray={inputObjectArray}
                                        previousValues={serviceData}
                                        assetType={asset}
                                        onFieldsMatched={(fields) =>
                                            setAssetRecord((prev) => ({ ...prev, ...fields }))
                                        }
                                    >
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={aiAssistLoading ? <CircularProgress size={14} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
                                            onClick={handleAIAssist}
                                            disabled={aiAssistLoading}
                                            sx={{
                                                textTransform: "none",
                                                fontSize: "12px",
                                                padding: "3px 10px",
                                                borderColor: "#3f51b5",
                                                color: "#3f51b5",
                                                whiteSpace: "nowrap",
                                                "&:hover": {
                                                    borderColor: "#3f51b5",
                                                    backgroundColor: "rgba(63,81,181,0.08)",
                                                },
                                            }}
                                        >
                                            {aiAssistLoading ? "Suggesting…" : "AI Assist"}
                                        </Button>
                                    </VoiceInput>
                                )}
                                {inputObjectArray && renderForm(inputObjectArray, asset, serviceData)}
                                {/* show files */}
                                {inputFiles.length !== 0
                                    && inputFiles.map((x, fileIndex) => removeFileArray.includes(x.id)
                                        ? <div style={{ background: '#00000036', borderRadius: '6px' }}>
                                            <div style={{ fontWeight: '600', textAlign: 'center' }}>Are you sure you want to remove?</div>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <span className='spanDeleteFile' style={{ color: 'green', paddingRight: '20px' }} onClick={() => removeFile(x.id)}>Yes</span>
                                                <span className='spanDeleteFile' style={{ color: 'red', paddingLeft: '20px' }} onClick={() => cancelRemoveFile(x.id)}>No</span>
                                            </div>
                                        </div>
                                        : <div key={x.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div className='filePill' style={{ marginBottom: '0px', marginTop: '10px' }}>
                                                <div style={{ display: 'flex' }}>
                                                    <InsertDriveFileOutlinedIcon style={{ color: "white", marginBottom: 'auto', marginTop: 'auto' }} />
                                                    <div style={{ paddingLeft: '8px' }}>
                                                        <div><b style={{ fontWeight: 600 }}></b>{x.AttachmentName.includes('.jpeg') || x.AttachmentName.includes('.jpg') || x.AttachmentName.includes('.png') ? `Image to be added ${fileIndex + 1}` : `File to be added ${fileIndex + 1}`}</div>
                                                        {/* <div><b style={{ fontWeight: 600 }}>Name: </b>{x.AttachmentName}</div> */}
                                                        {/* <div><b style={{ fontWeight: 600 }}>Description: </b>{x.AttachmentDescription}</div> */}
                                                    </div>
                                                </div>
                                                <CloseIcon color="secondary" style={{ marginTop: 'auto', marginBottom: 'auto', cursor: 'pointer' }} onClick={() => setRemoveFileFlag(x.id)} />
                                            </div>
                                        </div>)
                                }
                                {renderFileUpload()}
                                <div className="buttonBar">
                                    {isSubmitting ? (
                                        <div style={{ display: "flex", justifyContent: "center" }}>
                                            <CircularProgress />
                                        </div>
                                    ) : (
                                        <div>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className="ButtonShadow"
                                                disabled={isSubmitting}
                                                onClick={(e) => onSubmit(e, asset)}
                                            >
                                                Submit
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                className="ButtonShadow"
                                                onClick={() => setNotEditingInfo(true)}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {isErrors && (
                                    <div className="errorBarMessage">
                                        There are errors in the Form!
                                    </div>
                                )}
                            </form>
                        )}
                    </TabPane>

                    {/* INSPECTIONS */}
                    {isEdit && !asset.includes("WaterMeter") && (
                        <TabPane tabId="2" id="mapFormTab2">
                            {!selectInspection ? (
                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                    {canWrite && (
                                        <div style={{ padding: "10px 5px", margin: "auto" }}>
                                            <Button variant="contained" color="primary" onClick={addInspection}>
                                                Add Inspection
                                            </Button>
                                        </div>
                                    )}
                                    {isReadOnly && (
                                        <div style={{
                                            padding: "12px",
                                            margin: "10px 5px",
                                            backgroundColor: "#fff3e0",
                                            borderRadius: "4px",
                                            textAlign: "center",
                                            color: "#e65100",
                                            fontSize: "14px"
                                        }}>
                                            🔒 View Only Mode - Adding inspections is disabled
                                        </div>
                                    )}
                                    <Divider />
                                    <h4
                                        style={{
                                            padding: "2px 5px",
                                            margin: "auto",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                        }}
                                    >
                                        Inspection History
                                    </h4>
                                    {getInspections()}
                                </div>
                            ) : notEditingInspection && inspectionRecord[`${asset}ID`] ? (
                                <div>
                                    {inspectionObjectArray && renderTextFields(inspectionObjectArray, inspectionRecord)}
                                    {/* show uploaded files */}
                                    {isReadOnly && (
                                        <div style={{
                                            padding: "12px",
                                            margin: "10px 0",
                                            backgroundColor: "#fff3e0",
                                            borderRadius: "4px",
                                            textAlign: "center",
                                            color: "#e65100",
                                            fontSize: "14px"
                                        }}>
                                            🔒 View Only Mode - Editing is disabled
                                        </div>
                                    )}
                                    <div className="buttonBar">
                                        {canWrite && (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className="ButtonShadow"
                                                onClick={() => setNotEditingInspection(false)}
                                            >
                                                Edit
                                            </Button>
                                        )}
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            className="ButtonShadow"
                                            onClick={goToInspectionList}
                                        >
                                            Back
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <form>
                                        {inspectionObjectArray && renderForm(inspectionObjectArray, assetInspection, inspections)}
                                        {/* show uploaded files */}
                                        {inspectionInputFiles.length !== 0
                                            && inspectionInputFiles.map((x, fileIndex) => removeFileArray.includes(x.id)
                                                ? <div style={{ background: '#00000036', borderRadius: '6px' }}>
                                                    <div style={{ fontWeight: '600', textAlign: 'center' }}>Are you sure you want to remove?</div>
                                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <span className='spanDeleteFile' style={{ color: 'green', paddingRight: '20px' }} onClick={() => removeFile(x.id)}>Yes</span>
                                                        <span className='spanDeleteFile' style={{ color: 'red', paddingLeft: '20px' }} onClick={() => cancelRemoveFile(x.id)}>No</span>
                                                    </div>
                                                </div>
                                                : <div key={x.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <div className='filePill' style={{ marginBottom: '0px', marginTop: '10px' }}>
                                                        <div style={{ display: 'flex' }}>
                                                            <InsertDriveFileOutlinedIcon style={{ color: "white", marginBottom: 'auto', marginTop: 'auto' }} />
                                                            <div style={{ paddingLeft: '8px' }}>
                                                                <div><b style={{ fontWeight: 600 }}></b>{x.AttachmentName.includes('.jpeg') || x.AttachmentName.includes('.jpg') || x.AttachmentName.includes('.png') ? `Image to be added ${fileIndex + 1}` : `File to be added ${fileIndex + 1}`}</div>
                                                                {/* <div><b style={{ fontWeight: 600 }}>Name: </b>{x.AttachmentName}</div> */}
                                                                {/* <div><b style={{ fontWeight: 600 }}>Description: </b>{x.AttachmentDescription}</div> */}
                                                            </div>
                                                        </div>
                                                        <CloseIcon color="secondary" style={{ marginTop: 'auto', marginBottom: 'auto', cursor: 'pointer' }} onClick={() => setRemoveFileFlag(x.id)} />
                                                    </div>
                                                </div>)
                                        }
                                        {renderFileUpload(true)}
                                        <div className="buttonBar">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className="ButtonShadow"
                                                disabled={isSubmitting}
                                                onClick={(e) =>
                                                    onInspectionSubmit(e, `${asset}Inspection`)
                                                }
                                            >
                                                Submit
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                className="ButtonShadow"
                                                onClick={goToInspectionList}
                                            >
                                                Back
                                            </Button>
                                        </div>
                                    </form>
                                    {isErrors && (
                                        <div className="errorBarMessage">
                                            There are errors in the Form!
                                        </div>
                                    )}
                                </div>
                            )}
                        </TabPane>
                    )}
                </TabContent>
            </div>
            <Snackbar
                open={aiSnackbar.open}
                autoHideDuration={3000}
                onClose={() => setAiSnackbar({ open: false, message: "" })}
                message={aiSnackbar.message}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </div>
    );
}

export default AddToMap;
